/* eslint-disable */
// BBOT — MANUAL VELO ENTRY (streamline #3c, part b). The attested velo-entry path: when a velocity was
// taken by hand (a handheld radar gun BBOT couldn't auto-read), an admin enters it here. An attested
// reading is a TRUSTED measurement with a DISTINCT provenance from BBOT's own OCR — so a real reading is
// never lost to OCR failure, and is never confused with a machine read. Each entry is timestamped + who
// attested it; the emitted record maps 1:1 to the backend attested_to_measured_values (basis "Attested").
//
// Velo is asserted only when trusted: a clean on-screen-overlay read (BBOT auto) OR an attested manual
// entry (here). The plausibility band [40,110] is enforced (the backend firewall re-checks it). Identity
// of WHOSE velo it is stays the admin's act — this captures the NUMBER + its provenance, not the player.
//
// Note: this is for VELOCITIES only. 60-yard / Home-to-1st are TIMED events (seconds, from video), not
// radar — they are not entered here.
//
// DORMANT: not imported by index.js (ComingSoon intact). Inherits the locked design system; no new colors.
// PAGE-DOWN: on-screen sign-off (mobile legibility, flow) owed when the page is back up.
import React, { useState } from "react";
// SELF-CONTAINED SINCE bbotDesignSystem WAS DELETED (2026-09-02). That module was imported by
// nothing the app reaches and carried a SECOND palette that disagreed with App.js's `C` -- notably
// blueLight (#6a96e6 there, #dbe6fb live) -- so a brand change could land in the file nobody renders.
// The handful of tokens this component actually used are inlined at the values it was built against,
// so it still renders exactly as designed if it is ever wired in. Reconcile against `C` at that point.
const BRAND = { blue:"#2970b8", border:"#e5e9ef", coral:"#e8526a", ink:"#1e293b", white:"#ffffff", card:"#ffffff", shadowMd:"0 2px 6px rgba(71,120,214,.08)" };
const FONT = "'Inter',system-ui,sans-serif";
const SPACE = { sm:8, md:12, lg:16 };
const RADIUS = { sm:8, md:12 };
function card(extra){
  return Object.assign({
    background: BRAND.card, border: "1px solid " + BRAND.border,
    borderRadius: RADIUS.md, boxShadow: BRAND.shadowMd, padding: SPACE.lg,
    fontFamily: FONT, color: BRAND.ink, boxSizing: "border-box",
  }, extra || {});
}

const VMIN = 40, VMAX = 110;   // mirrors the backend velo firewall (per-age tighten @ Phase B)

// velo-bearing activities (timed running events are excluded — they are seconds, not radar)
export const VELO_ACTIVITIES = [
  { key: "hitting",  label: "Exit velo (hitting)" },
  { key: "pitching", label: "Fastball velo (pitching)" },
  { key: "throwing", label: "Throw / arm velo" },
  { key: "catcher",  label: "Catcher throw velo" },
];

function nowIso() { return new Date().toISOString(); }
function plausible(v) { return v !== "" && !isNaN(Number(v)) && Number(v) >= VMIN && Number(v) <= VMAX; }

// one attested-reading row
function VeloRow({ row, onChange, onRemove }) {
  const v = row.value;
  const bad = v !== "" && !plausible(v);
  return (
    <div style={{ display: "flex", gap: SPACE.sm, alignItems: "center", flexWrap: "wrap",
                  border: "1px solid " + BRAND.border, borderRadius: RADIUS.sm, padding: SPACE.sm }}>
      <select value={row.activity} onChange={(e) => onChange({ ...row, activity: e.target.value })}
              style={{ fontFamily: FONT, fontSize: 14, padding: "8px 10px", borderRadius: RADIUS.sm,
                       border: "1px solid " + BRAND.border, color: BRAND.ink, background: BRAND.white, minWidth: 170 }}>
        {VELO_ACTIVITIES.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
      </select>
      <div style={{ position: "relative" }}>
        <input type="number" inputMode="decimal" placeholder="mph" value={v}
               onChange={(e) => onChange({ ...row, value: e.target.value })}
               style={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, width: 90, padding: "8px 10px",
                        borderRadius: RADIUS.sm, color: BRAND.ink, textAlign: "center",
                        border: "1.5px solid " + (bad ? BRAND.coral : BRAND.border) }} />
        <span style={{ marginLeft: 4, fontSize: 12, color: BRAND.ink3 }}>mph</span>
      </div>
      <input type="text" placeholder="note (optional — e.g. jersey, rep)" value={row.note || ""}
             onChange={(e) => onChange({ ...row, note: e.target.value })}
             style={{ fontFamily: FONT, fontSize: 13, padding: "8px 10px", borderRadius: RADIUS.sm,
                      border: "1px solid " + BRAND.border, color: BRAND.ink, flex: 1, minWidth: 120 }} />
      <button onClick={onRemove} title="remove"
              style={{ border: "none", background: "transparent", color: BRAND.ink3, fontSize: 18,
                       cursor: "pointer", fontFamily: FONT, lineHeight: 1, padding: "0 6px" }}>×</button>
      {bad && <div style={{ flexBasis: "100%", fontSize: 11.5, color: BRAND.coral, fontWeight: 600 }}>
        outside {VMIN}–{VMAX} mph — check the reading (the system won't file an implausible velo)
      </div>}
    </div>
  );
}

// orchestrator. attestedBy = the admin's name/id; onSubmit(record) gets the attested batch.
export function BbotManualVeloEntry({ sessionId, attestedBy = "", onSubmit }) {
  const [rows, setRows] = useState([{ id: 1, activity: "hitting", value: "", note: "" }]);
  const [done, setDone] = useState(false);
  // A SWALLOWED FAILURE IS STILL A SILENT DISCARD. Gating `done` on the promise stopped the form
  // claiming success on a 403, but the catch said NOTHING -- the button just sat there and the
  // typed readings went nowhere with no way for the operator to know. The failure needs a voice.
  const [err, setErr] = useState("");

  function addRow() { setRows((r) => [...r, { id: (r[r.length - 1]?.id || 0) + 1, activity: "hitting", value: "", note: "" }]); }
  function setRow(i, nr) { setRows((r) => r.map((x, j) => (j === i ? nr : x))); }
  function removeRow(i) { setRows((r) => r.filter((_, j) => j !== i)); }

  const valid = rows.filter((r) => plausible(r.value));
  const anyBad = rows.some((r) => r.value !== "" && !plausible(r.value));
  const canSubmit = valid.length > 0 && !anyBad;

  function submit() {
    const at = nowIso();
    const record = {
      sessionId: sessionId || null,
      attestedBy: attestedBy || null,
      attestedAt: at,
      source: "attested_manual",   // distinct provenance from BBOT OCR -> backend basis "Attested"
      entries: valid.map((r) => ({ activity: r.activity, value: Number(r.value),
                                   note: r.note || null, attestedAt: at })),
    };
    // DO NOT DECLARE SUCCESS BEFORE THE WRITE RESOLVES. This set `done` immediately and then
    // called onSubmit, so the form said "saved" whether the write succeeded, 403'd or never
    // happened -- the exact silent-discard this component was held back from shipping to avoid.
    // Tested live: the proxy returned 403 and the UI still reported the readings recorded.
    setErr("");
    var pr = onSubmit && onSubmit(record);
    if (pr && typeof pr.then === "function") {
      pr.then(function(){ setDone(true); })
        .catch(function(e){
          // Rows stay editable so nothing typed is lost, and the operator is TOLD.
          setDone(false);
          setErr((e && e.message) ? String(e.message) : "the save did not go through");
        });
    } else {
      setDone(true);
    }
  }

  // The batch flattened to the rows the table stores. ONE ROW PER READING, because a reading is what
  // gets aggregated and disputed -- the batch is a UI convenience, not the unit. `basis` stays
  // "Attested" and is never the caller's to choose: a human said so, a device did not measure it.
  BbotManualVeloEntry.toRows = function(record){
    return (record.entries || []).map(function(e){
      return { session_id: record.sessionId || null,
               activity: e.activity,
               value_mph: Number(e.value),
               note: e.note || null,
               source: record.source || "attested_manual",
               basis: "Attested",
               attested_by: record.attestedBy || "",
               attested_at: e.attestedAt || record.attestedAt };
    });
  };

  // WIDTH FOLLOWS THE HOST, IT DOES NOT SET ITS OWN. At maxWidth 600 centred inside Run BBOT's
  // 760px column this sat visibly narrower than the card above it and read as a detached fragment.
  // A component that hard-codes a width cannot sit correctly in a container it does not know about.
  //
  // AND THIS COMMENT IS A // COMMENT, NOT A {/* */} ONE. As a JSX comment it sat as a SIBLING of the
  // returned div -- two roots in one return -- which fails the build. `node --check` does not parse
  // JSX, so it passed the check and broke the deploy silently: two commits sat on main unbuilt while
  // the live bundle stayed on the previous one.
  return (
    <div style={{ ...card(), width: "100%", boxSizing: "border-box" }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: BRAND.ink }}>Manual velo entry</div>
      <p style={{ fontFamily: FONT, fontSize: 13, color: BRAND.ink2, lineHeight: 1.5, margin: SPACE.sm + "px 0 " + SPACE.md + "px" }}>
        Enter a velocity you took by hand (handheld radar gun) that BBOT didn't read on-screen. These are
        recorded as <strong>attested measurements</strong> — trusted, and kept separate from the velocities
        BBOT reads automatically from an on-screen radar overlay.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: SPACE.sm }}>
        {rows.map((r, i) => <VeloRow key={r.id} row={r} onChange={(nr) => setRow(i, nr)} onRemove={() => removeRow(i)} />)}
      </div>
      <button onClick={addRow}
              style={{ marginTop: SPACE.sm, background: "transparent", border: "1px dashed " + BRAND.blue,
                       color: BRAND.blue, fontFamily: FONT, fontWeight: 700, fontSize: 13, borderRadius: RADIUS.sm,
                       padding: "8px 12px", cursor: "pointer" }}>+ Add reading</button>

      <div style={{ display: "flex", alignItems: "center", gap: SPACE.md, marginTop: SPACE.md,
                    borderTop: "1px solid " + BRAND.border, paddingTop: SPACE.md }}>
        <button disabled={!canSubmit} onClick={submit}
                style={{ padding: "12px 18px", borderRadius: RADIUS.md, border: "none", fontFamily: FONT,
                         fontSize: 15, fontWeight: 700, cursor: canSubmit ? "pointer" : "not-allowed",
                         background: canSubmit ? BRAND.coral : BRAND.border, color: canSubmit ? "#fff" : BRAND.ink3 }}>
          Attest &amp; save {valid.length ? `(${valid.length})` : ""}
        </button>
        {done && !err && <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: BRAND.blue }}>
          ✓ {valid.length} attested reading{valid.length === 1 ? "" : "s"} saved (basis: Attested)
        </span>}
        {err && <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: BRAND.coral }}>
          Not saved — {err}. Your readings are still here; try again.
        </span>}
      </div>
    </div>
  );
}

export function VeloEntryPreview() {
  return <BbotManualVeloEntry sessionId="demo" attestedBy="admin@643"
                              onSubmit={(r) => console.log("attested velo record:", r)} />;
}
